/**
 * SPEC-20260507-1400 (Fase 4): editor de step `script.exec`.
 *
 * Renderizado pelo SagaDefinitionEditor quando step.type === 'script.exec'.
 * 4 botões IA (validate execute/rollback, generate execute/rollback) +
 * description + select language + 2 textareas (execute / rollback).
 *
 * Rollback OBRIGATÓRIO — backend rejeita se vazio.
 */

import { useState } from "react";
import { Sparkles, ShieldCheck, Loader2, AlertTriangle, Wand2, KeyRound, X, MessageCircleQuestion } from "lucide-react";
import {
  validateScript,
  generateScriptInteractive,
  answerScriptInteractive,
  type ValidationResult,
  type AIOptions,
  type DeclaredPlaceholder,
  type DeclaredSecret,
  type InteractiveScriptResponse,
} from "../../services/ai";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import type {
  SagaStepDefinition,
  ScriptPlaceholderDecl,
  ScriptSecretDecl,
} from "../../types/saga-definition";
import { AIProviderSelect } from "../ai/AIProviderSelect";
import { toast } from "sonner";

export interface ScriptStepEditorProps {
  step: SagaStepDefinition;
  onUpdate(patch: Partial<SagaStepDefinition>): void;
  readOnly?: boolean;
}

type LoadingKind = "validate-execute" | "validate-rollback" | "generate-execute" | "generate-rollback" | null;

interface InteractiveQuestion {
  name: string;
  question: string;
  expectedType: "text" | "number" | "choice" | "secret-path";
  choices?: string[];
  default?: string;
  hint?: string;
}

interface InteractiveState {
  kind: "execute" | "rollback";
  sessionId: string;
  pendingMessageId: string;
  /** SEMPRE array — batch de N perguntas (IA agrupa pra reduzir round-trips). */
  questions: InteractiveQuestion[];
  /** Histórico de Q&A pra UI mostrar progresso. */
  history: Array<{ question: string; answer: string }>;
  /** Estado "thinking" enquanto espera próxima resposta da IA. */
  thinking?: boolean;
}

export function ScriptStepEditor({ step, onUpdate, readOnly = false }: ScriptStepEditorProps) {
  const [loading, setLoading] = useState<LoadingKind>(null);
  const [validation, setValidation] = useState<{ kind: "execute" | "rollback"; result: ValidationResult } | null>(null);
  const [aiOpts, setAiOpts] = useState<AIOptions>({ tools: { enabled: true, askUserEnabled: true } });
  const [interactive, setInteractive] = useState<InteractiveState | null>(null);
  // SPEC-20260507-2030: sessão IA do step. Quando user gera execute, salvamos
  // sessionId. Quando user gera rollback, REUSAMOS a mesma sessão — IA tem
  // todo o contexto (Q&A do execute + script gerado) automaticamente, então
  // escreve um rollback que reverte EXATAMENTE o execute.
  const [aiSessionId, setAiSessionId] = useState<string | null>(null);
  const language = step.scriptLanguage ?? "bash";
  const description = step.scriptDescription ?? "";
  const scriptExecute = step.scriptExecute ?? "";
  const scriptRollback = step.scriptRollback ?? "";

  async function handleValidate(kind: "execute" | "rollback") {
    if (!description.trim()) return toast.error("Defina o objetivo do script primeiro");
    if (kind === "execute" && !scriptExecute.trim()) return toast.error("Script de execução vazio");
    if (kind === "rollback" && !scriptRollback.trim()) return toast.error("Script de rollback vazio");
    setLoading(`validate-${kind}` as LoadingKind);
    try {
      const result = await validateScript({
        language,
        description,
        scriptExecute,
        scriptRollback,
        kind,
        options: aiOpts,
      });
      setValidation({ kind, result });
      toast[result.valid ? "success" : "warning"](
        result.valid
          ? `IA validou ${kind} — sem issues críticos`
          : `IA encontrou ${result.issues.length} issue(s) em ${kind}`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha na validação");
    } finally {
      setLoading(null);
    }
  }

  async function handleGenerate(kind: "execute" | "rollback") {
    if (!description.trim()) return toast.error("Defina o objetivo do script primeiro");
    if (kind === "rollback" && !scriptExecute.trim()) {
      return toast.error("Pra gerar rollback, primeiro escreva o script de execução");
    }
    setLoading(`generate-${kind}` as LoadingKind);
    try {
      // SPEC-20260507-2030: usa generate-script-interactive (sessão chat com
      // ask_user habilitado). IA pode pausar pra perguntar valores faltantes.
      // Se já temos sessão (do execute), reusa pra rollback — preserva contexto.
      const reuseSession = kind === "rollback" && aiSessionId !== null;
      const result = await generateScriptInteractive({
        language,
        description,
        scriptExecute: kind === "rollback" ? scriptExecute : undefined,
        kind,
        options: aiOpts,
        ...(reuseSession ? { existingSessionId: aiSessionId } : {}),
      });
      // Salva sessionId pra rollback futuro reusar.
      setAiSessionId(result.sessionId);
      handleInteractiveResponse(kind, result, []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha na geração");
    } finally {
      setLoading(null);
    }
  }

  function removePlaceholder(name: string) {
    onUpdate({ placeholders: (step.placeholders ?? []).filter((p) => p.name !== name) });
  }
  function removeSecret(name: string) {
    onUpdate({ secrets: (step.secrets ?? []).filter((s) => s.name !== name) });
  }
  function updatePlaceholderValue(name: string, value: string) {
    onUpdate({
      placeholders: (step.placeholders ?? []).map((p) =>
        p.name === name ? { ...p, value: value === "" ? undefined : value } : p,
      ),
    });
  }

  /**
   * Processa resposta do flow interactive: ou abre modal de pergunta (status=awaiting_input)
   * ou aplica script final no editor (status=ok).
   */
  function handleInteractiveResponse(
    kind: "execute" | "rollback",
    result: InteractiveScriptResponse,
    history: Array<{ question: string; answer: string }>,
  ): void {
    if (result.status === "awaiting_input" && result.pendingToolCall) {
      // IA está perguntando — abre modal. Suporta formato batch (questions[])
      // E legacy single (question + expectedType).
      const ptc = result.pendingToolCall;
      const questions: InteractiveQuestion[] = ptc.questions
        ? ptc.questions
        : ptc.question && ptc.expectedType
          ? [
              {
                name: "answer",
                question: ptc.question,
                expectedType: ptc.expectedType,
                ...(ptc.choices ? { choices: ptc.choices } : {}),
              },
            ]
          : [];
      if (questions.length === 0) {
        toast.error("Pendência sem perguntas — verifique log do backend");
        setInteractive(null);
        return;
      }
      setInteractive({
        kind,
        sessionId: result.sessionId,
        pendingMessageId: result.assistantMessage.id,
        questions,
        history,
      });
      return;
    }
    // status=ok → aplica script no editor
    if (!result.script) {
      toast.error("IA terminou sem retornar script — verifique a sessão pra debug");
      setInteractive(null);
      return;
    }
    const patch: Partial<SagaStepDefinition> =
      kind === "execute" ? { scriptExecute: result.script } : { scriptRollback: result.script };
    if (result.declaredSideChannel?.placeholders?.length) {
      patch.placeholders = mergePlaceholders(step.placeholders, result.declaredSideChannel.placeholders);
    }
    if (result.declaredSideChannel?.secrets?.length) {
      patch.secrets = mergeSecrets(step.secrets, result.declaredSideChannel.secrets);
    }
    onUpdate(patch);
    setInteractive(null);
    const qaCount = history.length;
    const extras: string[] = [];
    if (result.declaredSideChannel?.placeholders?.length)
      extras.push(`${result.declaredSideChannel.placeholders.length} placeholder(s)`);
    if (result.declaredSideChannel?.secrets?.length)
      extras.push(`${result.declaredSideChannel.secrets.length} secret(s)`);
    toast.success(
      `IA gerou script de ${kind}${qaCount > 0 ? ` após ${qaCount} pergunta(s)` : ""}${
        extras.length > 0 ? " — declarou " + extras.join(" + ") : ""
      }`,
    );
  }

  async function handleInteractiveAnswers(answers: Record<string, string>): Promise<void> {
    if (!interactive) return;
    // Histórico: 1 entrada por pergunta respondida (preserva detalhe).
    const additions = interactive.questions.map((q) => ({
      question: q.question,
      answer: answers[q.name] ?? "",
    }));
    const newHistory = [...interactive.history, ...additions];
    // Mantém modal aberto com flag "thinking" enquanto espera próxima resposta.
    setInteractive({ ...interactive, history: newHistory, thinking: true });
    try {
      const result = await answerScriptInteractive(interactive.sessionId, interactive.pendingMessageId, {
        answers,
        options: aiOpts,
      });
      handleInteractiveResponse(interactive.kind, result, newHistory);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao continuar geração");
      setInteractive(null);
    }
  }

  function handleInteractiveCancel(): void {
    setInteractive(null);
    toast.info("Geração interativa cancelada — sessão chat permanece pra debug");
  }

  return (
    <div className="space-y-3 pt-2 border-t border-border-subtle">
      <div className="rounded border border-warning/30 bg-warning-soft/40 p-2 text-xs flex items-start gap-2">
        <AlertTriangle size={13} className="text-warning shrink-0 mt-0.5" />
        <span className="text-warning-soft-fg">
          <strong>SECURITY DEBT:</strong> scripts rodam no host do worker sem sandbox. Use apenas com
          RBAC restrito (operação revisada). Sandbox real (gVisor) vira SPEC própria.
        </span>
      </div>

      {!readOnly && (
        <div className="rounded border border-border-subtle bg-surface-subtle/40 p-2">
          <AIProviderSelect value={aiOpts} onChange={setAiOpts} />
        </div>
      )}

      <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
          Objetivo do script <span className="text-danger">*</span>
        </label>
        <textarea
          disabled={readOnly}
          value={description}
          onChange={(e) => onUpdate({ scriptDescription: e.target.value })}
          rows={2}
          placeholder="Ex: Provisiona índice Elasticsearch pro tenant; idempotente."
          className="w-full px-2 py-1.5 text-xs border border-border rounded bg-bg disabled:bg-surface-subtle"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
            Linguagem
          </label>
          <select
            disabled={readOnly}
            value={language}
            onChange={(e) =>
              onUpdate({
                scriptLanguage: e.target.value as
                  | "bash"
                  | "python"
                  | "node"
                  | "terraform"
                  | "k8s",
              })
            }
            className="w-full px-2 py-1.5 text-xs border border-border rounded bg-bg font-mono-tight"
          >
            <option value="bash">bash</option>
            <option value="python">python</option>
            <option value="node">node</option>
            <option value="terraform">terraform (HCL)</option>
            <option value="k8s">k8s (YAML manifest)</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
            Timeout (ms) — default 60000
          </label>
          <input
            disabled={readOnly}
            type="number"
            value={step.scriptTimeoutMs ?? ""}
            onChange={(e) => onUpdate({ scriptTimeoutMs: e.target.value ? Number(e.target.value) : undefined })}
            placeholder="60000"
            className="w-full px-2 py-1.5 text-xs border border-border rounded bg-bg font-mono-tight"
          />
        </div>
      </div>

      <ScriptArea
        label="Script de execução"
        value={scriptExecute}
        onChange={(v) => onUpdate({ scriptExecute: v })}
        readOnly={readOnly}
        onGenerate={() => void handleGenerate("execute")}
        onValidate={() => void handleValidate("execute")}
        loadingGenerate={loading === "generate-execute"}
        loadingValidate={loading === "validate-execute"}
        placeholder="#!/usr/bin/env bash&#10;set -euo pipefail&#10;input=$(echo $WOS_SAGA_INPUT_JSON | jq -r ...)&#10;..."
      />

      {aiSessionId && (
        <div className="rounded border border-info/30 bg-info-soft/30 p-2 text-xs text-info-soft-fg">
          <strong>Sessão IA ativa:</strong> ao gerar o rollback, IA terá o contexto completo
          (perguntas/respostas + script) do execute — vai reverter exatamente o que foi feito,
          sem repetir perguntas.
        </div>
      )}

      <ScriptArea
        label="Script de rollback (obrigatório)"
        value={scriptRollback}
        onChange={(v) => onUpdate({ scriptRollback: v })}
        readOnly={readOnly}
        onGenerate={() => void handleGenerate("rollback")}
        onValidate={() => void handleValidate("rollback")}
        loadingGenerate={loading === "generate-rollback"}
        loadingValidate={loading === "validate-rollback"}
        placeholder="# Reverte o que execute fez. Pode ser no-op explícito:&#10;# echo 'no-op — operação não-compensável'"
        required
      />

      {(() => {
        const orphans = findOrphanPlaceholders(step);
        if (orphans.length === 0) return null;
        return (
          <div className="rounded border border-danger/40 bg-danger-soft/40 p-2.5">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="text-danger shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-danger-soft-fg">
                  Placeholders/secrets usados no script mas NÃO declarados ({orphans.length})
                </p>
                <p className="text-[11px] text-danger-soft-fg/90 mt-0.5">
                  A saga vai falhar em runtime com{" "}
                  <code className="font-mono-tight">"placeholder não declarado"</code>. Cada{" "}
                  <code className="font-mono-tight">{"{{placeholder.X}}"}</code> /{" "}
                  <code className="font-mono-tight">{"{{secret.X}}"}</code> precisa estar declarado.
                </p>
                <ul className="mt-1.5 flex flex-wrap gap-1">
                  {orphans.map((n) => (
                    <li
                      key={n}
                      className="text-[11px] font-mono-tight px-1.5 py-0.5 rounded border border-danger/30 bg-bg"
                    >
                      {`{{${n}}}`}
                    </li>
                  ))}
                </ul>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => autoDeclareOrphans(step, orphans, onUpdate)}
                    className="mt-2 text-xs text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Wand2 size={11} /> Auto-declarar
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {(step.placeholders?.length ?? 0) > 0 && (
        <div className="rounded border border-info/30 bg-info-soft/30 p-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-info-soft-fg mb-1.5">
            <Wand2 size={12} /> Placeholders declarados ({step.placeholders!.length})
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left font-medium pb-1 pr-2">{`{{placeholder.<name>}}`}</th>
                <th className="text-left font-medium pb-1 pr-2">Tipo</th>
                <th className="text-left font-medium pb-1 pr-2">Default</th>
                <th className="text-left font-medium pb-1 pr-2">Valor (override)</th>
                <th className="text-left font-medium pb-1">Descrição</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {step.placeholders!.map((p) => (
                <tr key={p.name} className="border-t border-border-subtle/40 align-top">
                  <td className="py-1 pr-2 font-mono-tight">{`{{placeholder.${p.name}}}`}</td>
                  <td className="py-1 pr-2">{p.type}</td>
                  <td className="py-1 pr-2 font-mono-tight text-muted-foreground">{p.default ?? "—"}</td>
                  <td className="py-1 pr-2">
                    {readOnly ? (
                      <span className="font-mono-tight">{p.value ?? "—"}</span>
                    ) : (
                      <input
                        type="text"
                        value={p.value ?? ""}
                        onChange={(e) => updatePlaceholderValue(p.name, e.target.value)}
                        placeholder={p.default ?? "(usa hierarquia)"}
                        className="w-full px-1 py-0.5 text-[11px] border border-border rounded bg-bg font-mono-tight"
                      />
                    )}
                  </td>
                  <td className="py-1 pr-2">{p.description}</td>
                  <td className="py-1">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removePlaceholder(p.name)}
                        className="text-muted-foreground hover:text-danger"
                        title="Remover placeholder"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] mt-1 text-info-soft-fg/80">
            Hierarquia (mais específico vence): runtimeValues → saga_variables (tenant/saga/step) → step.value → step.default
          </p>
        </div>
      )}

      {(step.secrets?.length ?? 0) > 0 && (
        <div className="rounded border border-warning/30 bg-warning-soft/30 p-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-warning-soft-fg mb-1.5">
            <KeyRound size={12} /> Secrets declarados ({step.secrets!.length})
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left font-medium pb-1 pr-2">{`{{secret.<name>}}`}</th>
                <th className="text-left font-medium pb-1 pr-2">env</th>
                <th className="text-left font-medium pb-1 pr-2">path</th>
                <th className="text-left font-medium pb-1 pr-2">required</th>
                <th className="text-left font-medium pb-1">Descrição</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {step.secrets!.map((s) => (
                <tr key={s.name} className="border-t border-border-subtle/40">
                  <td className="py-1 pr-2 font-mono-tight">{`{{secret.${s.name}}}`}</td>
                  <td className="py-1 pr-2 font-mono-tight">{s.envVar ?? "(auto UPPER_SNAKE)"}</td>
                  <td className="py-1 pr-2 font-mono-tight text-muted-foreground">{s.secretPath}</td>
                  <td className="py-1 pr-2">{s.required ? "yes" : "no"}</td>
                  <td className="py-1 pr-2">{s.description}</td>
                  <td className="py-1">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeSecret(s.name)}
                        className="text-muted-foreground hover:text-danger"
                        title="Remover secret"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] mt-1 text-warning-soft-fg/80">
            Secrets devem ser configurados em saga_variables (tenant scope) ou injetados via runtimeValues no compose.
          </p>
        </div>
      )}

      {validation && (
        <div
          className={
            "rounded border p-2 text-xs " +
            (validation.result.valid
              ? "border-success/30 bg-success-soft/40 text-success-soft-fg"
              : "border-danger/30 bg-danger-soft/40 text-danger-soft-fg")
          }
        >
          <p className="font-medium mb-1">
            Validação IA do {validation.kind}:{" "}
            {validation.result.valid ? "✓ válido" : "✗ tem issues"}
          </p>
          {validation.result.issues.length > 0 && (
            <ul className="list-disc ml-4 space-y-0.5">
              {validation.result.issues.map((i, idx) => (
                <li key={idx}>
                  <strong>[{i.severity}]</strong>
                  {i.line ? ` linha ${i.line}:` : ""} {i.message}
                </li>
              ))}
            </ul>
          )}
          {validation.result.suggestions.length > 0 && (
            <>
              <p className="font-medium mt-1.5 mb-0.5">Sugestões:</p>
              <ul className="list-disc ml-4 space-y-0.5">
                {validation.result.suggestions.map((s, idx) => (
                  <li key={idx}>{s}</li>
                ))}
              </ul>
            </>
          )}
          <p className="mt-1 text-muted-foreground">
            via {validation.result.provider} / {validation.result.model} ·{" "}
            {validation.result.usage.inputTokens}+{validation.result.usage.outputTokens} tokens
          </p>
        </div>
      )}

      {interactive && (
        <InteractiveQuestionModal
          state={interactive}
          onAnswers={handleInteractiveAnswers}
          onCancel={handleInteractiveCancel}
        />
      )}
    </div>
  );
}

interface InteractiveQuestionModalProps {
  state: InteractiveState;
  onAnswers(answers: Record<string, string>): void;
  onCancel(): void;
}

function InteractiveQuestionModal({ state, onAnswers, onCancel }: InteractiveQuestionModalProps) {
  // Inicializa cada answer com default da pergunta (se houver).
  const [answers, setAnswersState] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const q of state.questions) {
      if (q.default !== undefined) init[q.name] = q.default;
    }
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const isThinking = state.thinking === true;

  function setAnswer(name: string, value: string): void {
    setAnswersState((prev) => ({ ...prev, [name]: value }));
  }

  function allAnswered(): boolean {
    return state.questions.every((q) => {
      const v = answers[q.name];
      return v !== undefined && v.trim().length > 0;
    });
  }

  async function handleSubmit() {
    if (!allAnswered()) return;
    setSubmitting(true);
    try {
      // Trim antes de enviar.
      const cleaned: Record<string, string> = {};
      for (const q of state.questions) {
        cleaned[q.name] = (answers[q.name] ?? "").trim();
      }
      await onAnswers(cleaned);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-surface-1 rounded-lg shadow-lg w-full max-w-2xl p-4 border border-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircleQuestion size={16} className="text-primary" />
          <h2 className="font-medium text-sm">
            Geração interativa de {state.kind}
            {state.questions.length > 1 && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({state.questions.length} perguntas)
              </span>
            )}
          </h2>
        </div>

        {state.history.length > 0 && (
          <details className="mb-3 text-xs" open={false}>
            <summary className="cursor-pointer text-muted-foreground">
              Histórico ({state.history.length} resposta{state.history.length > 1 ? "s" : ""})
            </summary>
            <div className="mt-2 space-y-1.5 pl-2">
              {state.history.map((qa, i) => (
                <div key={i} className="border-l-2 border-border pl-2">
                  <p className="text-muted-foreground">{qa.question}</p>
                  <p className="font-mono-tight">→ {qa.answer}</p>
                </div>
              ))}
            </div>
          </details>
        )}

        {isThinking ? (
          <div className="flex items-center gap-2 py-6 justify-center text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            IA está pensando…
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {state.questions.map((q, idx) => (
                <div key={q.name}>
                  <Label htmlFor={`q-${q.name}`} className="text-sm font-medium">
                    {state.questions.length > 1 && (
                      <span className="text-muted-foreground mr-1">{idx + 1}.</span>
                    )}
                    {q.question}
                  </Label>
                  {q.hint && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">{q.hint}</p>
                  )}
                  {q.expectedType === "choice" && q.choices ? (
                    <div className="mt-1.5 space-y-1">
                      {q.choices.map((choice) => (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => setAnswer(q.name, choice)}
                          className={
                            "w-full text-left px-3 py-2 text-sm rounded border transition-colors " +
                            (answers[q.name] === choice
                              ? "border-primary bg-primary-soft text-primary-soft-fg"
                              : "border-border hover:bg-surface-2")
                          }
                        >
                          {choice}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Input
                      id={`q-${q.name}`}
                      type={q.expectedType === "number" ? "number" : "text"}
                      value={answers[q.name] ?? ""}
                      onChange={(e) => setAnswer(q.name, e.target.value)}
                      placeholder={hintForType(q.expectedType)}
                      autoFocus={idx === 0}
                      className="mt-1.5"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && allAnswered()) {
                          e.preventDefault();
                          void handleSubmit();
                        }
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
                Cancelar
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={!allAnswered() || submitting}>
                {submitting && <Loader2 size={12} className="animate-spin" />}
                {state.questions.length === 1 ? "Responder" : `Responder todas (${state.questions.length})`}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function hintForType(t: "text" | "number" | "choice" | "secret-path"): string {
  switch (t) {
    case "number":
      return "Ex: 10";
    case "secret-path":
      return "secret://wynk/mysql/admin-password";
    case "choice":
      return "";
    case "text":
    default:
      return "Sua resposta…";
  }
}

interface ScriptAreaProps {
  label: string;
  value: string;
  onChange(v: string): void;
  readOnly: boolean;
  onGenerate: () => void;
  onValidate: () => void;
  loadingGenerate: boolean;
  loadingValidate: boolean;
  placeholder: string;
  required?: boolean;
}

function ScriptArea({
  label,
  value,
  onChange,
  readOnly,
  onGenerate,
  onValidate,
  loadingGenerate,
  loadingValidate,
  placeholder,
  required,
}: ScriptAreaProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onGenerate}
              disabled={loadingGenerate}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait"
              title="Gerar script via IA a partir do objetivo"
            >
              {loadingGenerate ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              Gerar IA
            </button>
            <button
              type="button"
              onClick={onValidate}
              disabled={loadingValidate}
              className="text-xs text-foreground hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait"
              title="Validar script via IA (análise estática + teste mental)"
            >
              {loadingValidate ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
              Validar IA
            </button>
          </div>
        )}
      </div>
      <textarea
        disabled={readOnly}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={8}
        placeholder={placeholder}
        spellCheck={false}
        className="w-full px-2 py-1.5 text-xs border border-border rounded bg-bg font-mono-tight leading-relaxed disabled:bg-surface-subtle"
      />
    </div>
  );
}

/**
 * SPEC-20260507-2030 + 2300: scan placeholders `{{placeholder.X}}` e
 * `{{secret.X}}` em scriptExecute + scriptRollback e retorna nomes (com
 * prefixo do namespace) que NÃO estão declarados. Esses são "fantasmas"
 * — saga vai falhar em runtime.
 */
function findOrphanPlaceholders(step: SagaStepDefinition): string[] {
  const usedP = new Set<string>();
  const usedS = new Set<string>();
  const re = /\{\{(placeholder|secret)\.([a-zA-Z][a-zA-Z0-9_]*)\}\}/g;
  for (const script of [step.scriptExecute, step.scriptRollback]) {
    if (!script) continue;
    let m: RegExpExecArray | null;
    while ((m = re.exec(script)) !== null) {
      if (m[1] === "placeholder") usedP.add(m[2]);
      else usedS.add(m[2]);
    }
  }
  if (usedP.size === 0 && usedS.size === 0) return [];
  const declP = new Set((step.placeholders ?? []).map((p) => p.name));
  const declS = new Set((step.secrets ?? []).map((s) => s.name));
  const ghosts: string[] = [];
  for (const n of usedP) if (!declP.has(n)) ghosts.push(`placeholder.${n}`);
  for (const n of usedS) if (!declS.has(n)) ghosts.push(`secret.${n}`);
  return ghosts.sort();
}

/**
 * Auto-declara placeholders/secrets fantasmas. Heurística simples — operador
 * pode ajustar depois. SPEC-2300: orphans vêm como "placeholder.X" / "secret.X".
 */
function autoDeclareOrphans(
  step: SagaStepDefinition,
  orphans: string[],
  onUpdate: (patch: Partial<SagaStepDefinition>) => void,
): void {
  const newPlaceholders: ScriptPlaceholderDecl[] = [];
  const newSecrets: ScriptSecretDecl[] = [];
  const looksLikeSecret = (n: string) =>
    /(?:password|secret|token|api_?key|private_?key|credential)/i.test(n);
  for (const ghost of orphans) {
    const [kind, name] = ghost.split(".", 2);
    if (kind === "secret" || (kind === "placeholder" && looksLikeSecret(name))) {
      newSecrets.push({
        name,
        envVar: toUpperSnake(name),
        secretPath: `secret://wos/auto/${name.toLowerCase()}`,
        description: "Auto-declarado. Ajuste secretPath conforme seu ESO/Vault.",
        required: true,
      });
    } else {
      newPlaceholders.push({
        name,
        source: "prompt-user-once",
        description: "Auto-declarado. Valor coletado uma vez no compose.",
        type: "string",
      });
    }
  }
  const patch: Partial<SagaStepDefinition> = {};
  if (newPlaceholders.length > 0)
    patch.placeholders = [...(step.placeholders ?? []), ...newPlaceholders];
  if (newSecrets.length > 0) patch.secrets = [...(step.secrets ?? []), ...newSecrets];
  onUpdate(patch);
}

function toUpperSnake(s: string): string {
  return s
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .toUpperCase();
}

function mergePlaceholders(
  existing: ScriptPlaceholderDecl[] | undefined,
  incoming: DeclaredPlaceholder[],
): ScriptPlaceholderDecl[] {
  const out = [...(existing ?? [])];
  for (const p of incoming) {
    const idx = out.findIndex((e) => e.name === p.name);
    const decl: ScriptPlaceholderDecl = {
      name: p.name,
      source: p.source,
      description: p.description,
      type: p.type as ScriptPlaceholderDecl["type"],
      ...(p.default !== undefined ? { default: p.default } : {}),
      ...(p.value !== undefined ? { value: p.value } : {}),
      ...(p.choices ? { choices: p.choices } : {}),
    };
    if (idx >= 0) out[idx] = decl;
    else out.push(decl);
  }
  return out;
}

function mergeSecrets(
  existing: ScriptSecretDecl[] | undefined,
  incoming: DeclaredSecret[],
): ScriptSecretDecl[] {
  const out = [...(existing ?? [])];
  for (const s of incoming) {
    const idx = out.findIndex((e) => e.name === s.name);
    const decl: ScriptSecretDecl = {
      name: s.name,
      ...(s.envVar ? { envVar: s.envVar } : {}),
      secretPath: s.secretPath,
      description: s.description,
      required: s.required,
    };
    if (idx >= 0) out[idx] = decl;
    else out.push(decl);
  }
  return out;
}
